import { useMutation, useQueryClient } from "@tanstack/react-query";
import {  notification } from "antd";
import { collection, addDoc, updateDoc, query, where, getDocs, doc } from "firebase/firestore";
import { db} from "../../firebase/firebase";
import { ApplyJobPayloadInterface } from "./dto";
import { APPLICATION_STATUS } from "enum/job-status-type";
import { QueryKeys } from "config/query-keys";

export const applyJobs = async ({ id, data, enumeratorId }: ApplyJobPayloadInterface) => {
  try {
    const jobApplicationCollectionRef = collection(db, "applications");
    // Check if an application already exists
    const existingQuery = query(
      jobApplicationCollectionRef,
      where("jobId", "==", id),
      where("applicantId", "==", enumeratorId)
    );

    const querySnapshot = await getDocs(existingQuery);

    if (!querySnapshot.empty) {
      // If an application exists, update it
      const existingDoc = querySnapshot.docs[0];
      const applicationRef = doc(db, "applications", existingDoc.id);

      await updateDoc(applicationRef, {
        resume: data.resume || existingDoc.data().resume,
        coverLetter: data.coverLetter || existingDoc.data().coverLetter,
        status: APPLICATION_STATUS.SUBMITTED,
        appliedAt: new Date(),
      });

      return { success: true, updated: true };
    }

    // If no existing application, create a new one
    const applicationRef = await addDoc(jobApplicationCollectionRef, {
      jobId: id,
      applicantId: enumeratorId,
      resume: data.resume || "",
      coverLetter: data.coverLetter || "",
      status: APPLICATION_STATUS.SUBMITTED,
      appliedAt: new Date(),
    });

    await updateDoc(applicationRef, { id: applicationRef.id });

    return { success: true, created: true };
  } catch (error) {
    console.error("Error applying for job:", error);
    throw error;
  }
};

export const useApplyJobs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: applyJobs,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.JOBS] });
    },
    onError: () => {
      notification.error({
        message: "Application Failed",
        description: "There was an issue applying for the job.",
      });
    },
  });
};
